import dayjs from 'dayjs';

// Timestamp formatters for table cells (FNP-924), moved with the Users module
// from flexinet-console-app. That copy used moment; this app already ships
// dayjs, so the same guard runs on dayjs instead of adding a second date library.
//
// ═══ A NULL TIMESTAMP MUST NOT RENDER AS A REAL ONE ═══
//
// `dayjs(undefined)`, like `moment(undefined)`, returns THE CURRENT DATE AND
// TIME. An unguarded formatter therefore renders a plausible timestamp of
// *right now* for a row whose value is null: fabricated data a user cannot tell
// from a genuine reading. Blank is visibly missing; "Aug 14, 2026 19:50" reads
// as fact.
//
// MODULE_FEATURE_CHECKLIST §13: "Nullable fields render a placeholder (—),
// never blank or `undefined`."

/** What an absent or unparseable timestamp renders as. */
export const TIMESTAMP_PLACEHOLDER = '—';

/**
 * Guards the absent-data cases that reach these formatters: null/undefined,
 * '' (how an absent AWSDateTime often arrives), a non-primitive, and a value
 * dayjs cannot parse. Epoch 0 is a REAL instant and must survive.
 */
const isRenderable = (ts?: unknown): ts is string | number => {
    if (ts === null || typeof ts === 'undefined') {
        return false;
    }
    if (typeof ts !== 'string' && typeof ts !== 'number') {
        return false;
    }
    if (typeof ts === 'string' && ts.trim() === '') {
        return false;
    }
    return dayjs(ts).isValid();
};

/**
 * The STRING form, for anything composed into other text. FNP-1029:
 * `formatTimestamp` returns JSX, and interpolating it renders "[object Object]".
 */
export const formatTimestampText = (ts?: unknown, format = 'MMM DD, YYYY HH:mm:ss'): string =>
    isRenderable(ts)
        ? dayjs(ts).format(format)
        : TIMESTAMP_PLACEHOLDER;

/** The JSX form, for table cells. One null-guard, shared with the text form. */
export const formatTimestamp = (ts?: unknown, format = 'MMM DD, YYYY HH:mm:ss') => (
    <>{formatTimestampText(ts, format)}</>
);
